<template>
  <section v-if="products.length || isLoadingProducts" class="sf-featured px-4 sm:px-0 pb-10">
    <div v-if="section.settings.title || section.settings.subtitle" class="pb-5" :class="{ 'text-center': template === 'minimal' }">
      <h5 v-if="section.settings.title" class="sf-heading sf-category-title text-xl md:text-2xl font-bold">{{ section.settings.title }}</h5>
      <p v-if="section.settings.subtitle" class="sf-featured__subtitle text-sm mt-1">{{ section.settings.subtitle }}</p>
    </div>

    <div v-if="isLoadingProducts" class="sf-product-grid animate-pulse" :class="`sf-product-grid--${template}`">
      <div v-for="i in [1,2,3,4]" :key="i">
        <div class="sf-skeleton w-full h-40 sf-radius"></div>
        <p class="sf-skeleton h-4 mt-2 w-3/4"></p>
      </div>
    </div>

    <template v-else-if="style === 'carousel'">
      <swiper
        :slides-per-view="1.2"
        :space-between="16"
        :pagination="true"
        :free-mode="true"
        :modules="modules"
        :breakpoints="breakpoints"
        class="pb-10 sf-featured__carousel"
      >
        <swiper-slide v-for="product in products" :key="product.id" class="h-auto">
          <StoreProductCard :product="product" @select="(p: any) => emit('open-product', p)" />
        </swiper-slide>
      </swiper>
    </template>

    <StoreProductGrid v-else :products="products" @select="(p: any) => emit('open-product', p)" />
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Pagination, FreeMode } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/pagination'
import { categories } from '@/global/state/GlobalState'
import { FeaturedProductsSection, storefrontTemplate } from '@/global/state/StorefrontState'
import StoreProductCard from '../StoreProductCard.vue'
import StoreProductGrid from '../StoreProductGrid.vue'

/* global defineProps, defineEmits */
const props = defineProps<{ section: FeaturedProductsSection }>()
const emit = defineEmits(['open-product'])

const modules = [Pagination, FreeMode]
const template = storefrontTemplate
const style = computed(() => props.section.settings?.style ?? 'carousel')
const isLoadingProducts = computed(() => !categories.value && (props.section.settings?.productIds?.length ?? 0) > 0)

/** Resolve the configured ids against the cached catalog, keeping the configured order. */
const products = computed<any[]>(() => {
  const ids: number[] = props.section.settings?.productIds ?? []
  if (!ids.length || !categories.value) return []
  const byId = new Map<number, any>()
  categories.value.forEach((category: any) => {
    (category.products || []).forEach((product: any) => {
      if (!byId.has(Number(product.id))) byId.set(Number(product.id), product)
    })
  })
  return ids
    .map((id) => byId.get(Number(id)))
    .filter((product: any) => product && product.isActive !== false && !product.isDeleted)
})

const breakpoints = computed(() => {
  switch (template.value) {
    case 'grid':
    case 'dark':
      return { 0: { slidesPerView: 2.2, spaceBetween: 12 }, 768: { slidesPerView: 3.2, spaceBetween: 16 }, 1024: { slidesPerView: 4, spaceBetween: 20 } }
    case 'showcase':
      return { 0: { slidesPerView: 1.15, spaceBetween: 16 }, 768: { slidesPerView: 2.2, spaceBetween: 24 }, 1024: { slidesPerView: 3, spaceBetween: 28 } }
    case 'minimal':
      return { 0: { slidesPerView: 1.1, spaceBetween: 24 }, 768: { slidesPerView: 2, spaceBetween: 48 }, 1024: { slidesPerView: 3, spaceBetween: 56 } }
    default:
      return { 0: { slidesPerView: 1.1, spaceBetween: 16 }, 768: { slidesPerView: 2, spaceBetween: 32 }, 1024: { slidesPerView: 2.5, spaceBetween: 32 } }
  }
})
</script>

<style>
.sf-featured__subtitle {
  color: var(--sf-muted);
}
.sf-featured__carousel .swiper-slide {
  height: auto;
}
.sf-featured__carousel .sf-card--dark {
  height: 100%;
}
</style>
